import { type ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import cx from 'clsx';
import { FormattedMoney } from '@repo/design-system-kit';
import { UNIT_CODES } from '../../../../constants';
import { usePdfPreviewContext } from '../../pdf-preview-context.tsx';
import type { Item, Progress } from '../../types';
import { useTranslatePlaceholders } from '../../../hooks/use-translate-placeholders.ts';
import { usePdfPreviewColumns } from './use-pdf-preview-columns.ts';
import styles from './item-row.module.css';

interface ItemRowProps {
  item: Item;
  index: number;
  hideBorder?: boolean;
}

export function ItemRow({ hideBorder, item, index }: ItemRowProps): ReactNode {
  const { columns } = usePdfPreviewColumns();

  return (
    <tr
      className={cx(styles.row, hideBorder && styles['no-bottom'])}
      data-testid={`pdf-preview-items-row-${index}`}
    >
      {columns.map(column => (
        <ItemCell column={column.id} item={item} key={column.id} />
      ))}
    </tr>
  );
}

interface ItemCellProps {
  column: string;
  item: Item;
}

function ItemCell({ column, item }: ItemCellProps): ReactNode {
  if (column === 'description') {
    return <ItemDescriptionCell description={item.description} title={item.title} />;
  }

  if (column === 'quantity') {
    return <ItemQuantityCell quantity={item.quantity} unit={item.unit} />;
  }

  if (column === 'unit_price') {
    return <ItemUnitPriceCell unitPrice={item.unitPrice} />;
  }

  if (column === 'progress') {
    return <ItemProgressCell progress={item.progress} />;
  }

  if (column === 'cumulative_progress') {
    return <ItemCumulativeProgressCell progress={item.progress} />;
  }

  if (column === 'vat_rate') {
    return <ItemVatRateCell vatRate={item.vatRate} />;
  }

  if (column === 'total' || column === 'subtotal') {
    return <ItemTotalCell totalExcludingVat={item.totalExcludingVat} />;
  }

  return <td />;
}

interface ItemDescriptionCellProps {
  title?: string;
  description?: string;
}

function ItemDescriptionCell({ title, description }: ItemDescriptionCellProps): ReactNode {
  const { displayLanguage } = usePdfPreviewContext();
  const { translatePlaceholders } = useTranslatePlaceholders();

  return (
    <td className={cx('body-1', styles['description-cell'])}>
      <p className={styles.title} data-testid="pdf-preview-item-title">
        {title ? translatePlaceholders(title, displayLanguage) : null}
      </p>
      {description ? (
        <p className={styles.description} data-testid="pdf-preview-item-description">
          {translatePlaceholders(description, displayLanguage)}
        </p>
      ) : null}
    </td>
  );
}

interface ItemQuantityCellProps {
  quantity?: string;
  unit?: string;
}

function ItemQuantityCell({ quantity, unit }: ItemQuantityCellProps): ReactNode {
  const { formatMessage } = useIntl();
  const { displayLanguage } = usePdfPreviewContext();

  const formattedUnit = useMemo(() => {
    let qty = Math.abs(Number(quantity));
    if (displayLanguage === 'fr') {
      qty = qty > -2 && qty < 2 ? 1 : qty;
    }

    if (unit && UNIT_CODES.includes(unit)) {
      return formatMessage(
        { id: `pdf-preview.unit.${unit}` },
        { number: qty, language: displayLanguage }
      );
    }
    return unit ?? '';
  }, [displayLanguage, formatMessage, quantity, unit]);

  return (
    <td className={cx('body-1', styles['quantity-cell'])} data-testid="pdf-preview-item-quantity">
      {quantity} {formattedUnit}
    </td>
  );
}

interface ItemUnitPriceCellProps {
  unitPrice?: string;
}

function ItemUnitPriceCell({ unitPrice }: ItemUnitPriceCellProps): ReactNode {
  const { currency } = usePdfPreviewContext();

  return (
    <td className={cx('body-1', styles['amount-cell'])} data-testid="pdf-preview-item-unit-price">
      <FormattedMoney amount={unitPrice} currency={currency} token="money-symbol" />
    </td>
  );
}

interface ItemProgressCellProps {
  progress?: Progress;
}

function ItemProgressCell({ progress }: ItemProgressCellProps): ReactNode {
  const { currency } = usePdfPreviewContext();
  const { percentage, amount, type } = progress || {};

  if (!percentage && !amount) {
    // eslint-disable-next-line formatjs/no-literal-string-in-jsx -- empty cell
    return <td className={cx('body-1', styles['amount-cell'])}>-</td>;
  }

  switch (type) {
    case 'percentage':
      return (
        // eslint-disable-next-line formatjs/no-literal-string-in-jsx -- percentage
        <td className={cx('body-1', styles['amount-cell'])}>{`${percentage || 0}%`}</td>
      );
    case 'amount':
      return (
        <td className={cx('body-1', styles['amount-cell'])}>
          <FormattedMoney amount={amount?.value} currency={currency} token="money-symbol" />
        </td>
      );
    default:
      // eslint-disable-next-line formatjs/no-literal-string-in-jsx -- empty cell
      return <td className={cx('body-1', styles['amount-cell'])}>-</td>;
  }
}

interface ItemCumulativeProgressCellProps {
  progress?: Progress;
}

function ItemCumulativeProgressCell({ progress }: ItemCumulativeProgressCellProps): ReactNode {
  const { currency } = usePdfPreviewContext();
  const { type, cumulativePercentage, cumulativeAmount, percentage, amount } = progress || {};
  if (!cumulativePercentage && !cumulativeAmount && !percentage && !amount) {
    // eslint-disable-next-line formatjs/no-literal-string-in-jsx -- empty cell
    return <td className={cx('body-1', styles['amount-cell'])}>-</td>;
  }
  const totalCumulativeProgress =
    type === 'percentage'
      ? Number(cumulativePercentage ?? 0) + Number(percentage ?? 0)
      : Number(cumulativeAmount?.value ?? 0) + Number(amount?.value ?? 0);

  switch (type) {
    case 'percentage':
      return (
        <td className={cx('body-1', styles['amount-cell'])}>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx -- percentage */}
          {Number(totalCumulativeProgress).toFixed(2)}%
        </td>
      );
    case 'amount':
      return (
        <td className={cx('body-1', styles['amount-cell'])}>
          <FormattedMoney
            amount={totalCumulativeProgress}
            currency={currency}
            token="money-symbol"
          />
        </td>
      );
    default:
      // eslint-disable-next-line formatjs/no-literal-string-in-jsx -- empty cell
      return <td className={cx('body-1', styles['amount-cell'])}>-</td>;
  }
}

interface ItemVatRateCellProps {
  vatRate?: string;
}

function ItemVatRateCell({ vatRate }: ItemVatRateCellProps): ReactNode {
  const { formatNumber } = useIntl();

  return (
    <td className={cx('body-1', styles['vat-cell'])} data-testid="pdf-preview-item-vat-rate">
      {vatRate
        ? formatNumber(Number(vatRate), {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          })
        : null}
    </td>
  );
}

interface ItemTotalCellProps {
  totalExcludingVat?: string;
}

function ItemTotalCell({ totalExcludingVat }: ItemTotalCellProps): ReactNode {
  const { currency } = usePdfPreviewContext();

  return (
    <td
      className={cx('body-1', styles['subtotal-cell'])}
      data-testid="pdf-preview-item-total-amount"
    >
      <FormattedMoney amount={totalExcludingVat} currency={currency} token="money-symbol" />
    </td>
  );
}
